import { takeLatest, all, put, call } from 'redux-saga/effects';

import axios from '../../axiosInstance';
import requests from '../../requests';
import { getPlanRestrictions } from '../workspace/workspace.plans';

import { invitationActionTypes } from './invitation.types';
import {
    invitationVerifySuccess,
    invitationVerifyFailure,
    invitationAcceptSuccess,
    invitationAcceptFailure,
} from './invitation.actions';
import {
    workspaceInitSuccess,
    workspaceSwitchSuccess,
} from '../workspace/workspace.actions';

// Normalise an axios error into the { code, message } shape every failure
// action in this module expects.
function parseErr(e, fallbackMessage) {
    return {
        code:    e?.response?.data?.errorCode || 'UNKNOWN',
        message: e?.response?.data?.message   || e?.message || fallbackMessage,
    };
}

// Verify an invitation token. Public endpoint — never carries auth.
// Response: { data: { workspace_name, inviter_name, invitee_email } }
export function* verifyInvitation({ payload: { token } }) {
    try {
        const { data } = yield call(axios.get, `${requests.invitationVerify}?token=${token}`);
        yield put(invitationVerifySuccess(data?.data));
    } catch (e) {
        yield put(invitationVerifyFailure(parseErr(e, 'Invalid or expired invitation.')));
    }
}

// Accept an invitation. Requires authenticated session.
// Mirrors `createWorkspace` (src/redux/workspace/workspace.sagas.js) so the
// new workspace is in the local list, is the current workspace, is persisted
// server-side as the user's active workspace, and stale teams/members are
// cleared — all before the page redirects.
export function* acceptInvitation({ payload: { token } }) {
    try {
        // 1. POST -- accept. Backend joins the user to the workspace
        //    transactionally and returns the new workspace's id + name.
        const { data: acceptResp } = yield call(axios.post, requests.invitationAccept, { token });
        const wsId = acceptResp?.data?.workspace_id;
        const stub = { id: wsId };

        // 2. GET -- refetch list so `my_role` is authoritative.
        const { data: list } = yield call(axios.get, requests.workspaces);
        const newWorkspace = (list || []).find((w) => w.id === wsId) || stub;
        const planName = newWorkspace.plan_slug || 'free';
        const restrictions = getPlanRestrictions(planName);

        yield put(workspaceInitSuccess({
            list: list || [],
            currentWorkspace: newWorkspace,
            plan: planName,
            restrictions,
        }));

        // 3. PUT -- persist the active workspace server-side so future bootstraps
        //    land here. Dispatch WORKSPACE_SWITCH_SUCCESS afterwards to clear
        //    stale teams/members (init success doesn't touch those slices).
        yield call(axios.put, requests.userActiveWorkspace, { workspaceId: wsId });
        yield put(workspaceSwitchSuccess({
            currentWorkspace: newWorkspace,
            plan: planName,
            restrictions,
        }));

        // 4. Flip the UI flag — the Invitation page's useEffect on
        //    acceptStatus === 'success' performs history.push('/diagrams/').
        yield put(invitationAcceptSuccess());
    } catch (e) {
        yield put(invitationAcceptFailure(parseErr(e, 'Failed to accept invitation.')));
    }
}

// Watchers
export function* onVerifyInvitation() {
    yield takeLatest(invitationActionTypes.INVITATION_VERIFY_REQUEST, verifyInvitation);
}

export function* onAcceptInvitation() {
    yield takeLatest(invitationActionTypes.INVITATION_ACCEPT_REQUEST, acceptInvitation);
}

export function* invitationSagas() {
    yield all([
        call(onVerifyInvitation),
        call(onAcceptInvitation),
    ]);
}
